import {
  Button,
  Stack,
  Input,
  Typography,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { SingleSelect } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import React from 'react';
import * as R from 'ramda';
import isNil from 'lodash/isNil';
import { useController, useForm, useWatch } from 'react-hook-form';
import messages from '../messages';
import type { EditModeUIProps } from './types';
import { transformSubmittedValues } from './utils';
import useHasPermission from '../../../hooks/useHasPermission';

function EditModeUI({
  intl,
  onSaveFinalDecision,
  finalScreeningStatus,
  reasonForDecision,
  onCancelEdit,
}: EditModeUIProps) {
  const { control, handleSubmit, formState } = useForm({
    mode: 'onChange',
    defaultValues: {
      finalScreeningStatus,
      reasonForDecision,
    },
  });
  const finalScreeningStatusController = useController({
    control,
    name: 'finalScreeningStatus',
    rules: { required: true },
  });
  const finalScreeningStatusValue = useWatch({
    control,
    name: 'finalScreeningStatus',
  });
  const isReasonForDecisionRequired =
    finalScreeningStatusValue === 'APPROVED_WITH_CONDITIONS' ||
    finalScreeningStatusValue === 'DECLINED';
  const reasonForDecisionController = useController({
    control,
    name: 'reasonForDecision',
    rules: {
      validate: (value) => {
        if (isReasonForDecisionRequired) {
          return value?.trim() ? true : intl.formatMessage(messages.required);
        }
        return true;
      },
    },
  });
  const submitHandler = R.pipe(transformSubmittedValues, onSaveFinalDecision);

  const hasCanMakeFinalDecisionPermission = useHasPermission(
    'screening-application-save-final-decision',
  );
  const isStatusDropdownDisabled = !hasCanMakeFinalDecisionPermission;

  const isEdittingExistingDecision = !isNil(finalScreeningStatus);
  const shouldDisplayCancelButton = isEdittingExistingDecision;

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <Stack direction="row">
        <Stack width="22%" marginRight={3} spacing={1}>
          <Typography variant="labelLarge">
            {intl.formatMessage(messages.finalScreeningStatus)}
          </Typography>
          <Tooltip
            placement="top"
            variant="light"
            arrow={false}
            title={
              !hasCanMakeFinalDecisionPermission && (
                <Typography variant="titleSmall" fontWeight="bold">
                  {intl.formatMessage(
                    messages.finalDecisionPermissionRequiredTooltip,
                  )}
                </Typography>
              )
            }
            containerSX={{
              width: '100%',
            }}
          >
            <SingleSelect
              {...finalScreeningStatusController.field}
              disabled={isStatusDropdownDisabled}
              fullWidth
              helperText={intl.formatMessage(messages.required)}
              options={[
                {
                  value: 'APPROVED',
                  text: intl.formatMessage(messages.approved),
                },
                {
                  value: 'APPROVED_WITH_CONDITIONS',
                  text: intl.formatMessage(messages.approvedWithConditions),
                },
                {
                  value: 'DECLINED',
                  text: intl.formatMessage(messages.declined),
                },
              ]}
            />
          </Tooltip>
        </Stack>
        <Stack width="55%" spacing={2} marginRight={3}>
          <Typography
            variant="labelLarge"
            sx={{ marginBottom: '6px !important' }}
          >
            {intl.formatMessage(messages.reasonForDecision)}
          </Typography>
          <Input
            {...reasonForDecisionController.field}
            disabled={!hasCanMakeFinalDecisionPermission}
            error={
              isReasonForDecisionRequired &&
              !!reasonForDecisionController.fieldState.error
            }
            helperText={
              isReasonForDecisionRequired
                ? intl.formatMessage(messages.required)
                : undefined
            }
            multiline
            rows={3.5}
            placeholder="Start typing..."
          />
        </Stack>
        <Button
          type="submit"
          variant="primary"
          disabled={
            !hasCanMakeFinalDecisionPermission ||
            !formState.isValid ||
            formState.isSubmitting
          }
          marginLeft={3}
          sx={{
            height: '40px',
            marginTop: 3.3,
          }}
        >
          {intl.formatMessage(messages.saveFinalDecision)}
        </Button>
        {shouldDisplayCancelButton && (
          <Button
            variant="secondary"
            marginLeft={3}
            sx={{
              height: '40px',
              marginTop: 3.3,
            }}
            onClick={onCancelEdit}
          >
            {intl.formatMessage(messages.cancel)}
          </Button>
        )}
      </Stack>
    </form>
  );
}

export default EditModeUI;
